import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { CheckCircle, Clock, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import type { PredictionErrorEvaluationJob } from '@/lib/types/ds/prediction-error-evaluation';

interface JobStatusIndicatorProps {
  job: PredictionErrorEvaluationJob;
}

export function JobStatusIndicator({ job }: JobStatusIndicatorProps) {
  const progressPercent =
    job.total_combinations && job.total_combinations > 0
      ? Math.round(((job.completed_combinations ?? 0) / job.total_combinations) * 100)
      : 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <JobStatusBadge status={job.job_status} />
        {job.progress_message && (
          <span className="text-sm text-muted-foreground">{job.progress_message}</span>
        )}
      </div>

      {(job.job_status === 'running' || job.job_status === 'pending') && job.total_combinations && (
        <div className="space-y-1">
          <Progress value={progressPercent} className="h-2" />
          <p className="text-xs text-muted-foreground">
            {job.completed_combinations ?? 0} / {job.total_combinations} 組合せ完了 (
            {progressPercent}%)
          </p>
        </div>
      )}

      {job.job_status === 'failed' && job.error_message && (
        <p className="text-sm text-destructive">{job.error_message}</p>
      )}
    </div>
  );
}

export function JobStatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'completed':
      return (
        <Badge className="bg-green-500 hover:bg-green-600">
          <CheckCircle className="mr-1 h-3 w-3" />
          完了
        </Badge>
      );
    case 'running':
      return (
        <Badge className="bg-blue-500 hover:bg-blue-600">
          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          実行中
        </Badge>
      );
    case 'pending':
      return (
        <Badge variant="secondary">
          <Clock className="mr-1 h-3 w-3" />
          待機中
        </Badge>
      );
    case 'failed':
      return (
        <Badge variant="destructive">
          <XCircle className="mr-1 h-3 w-3" />
          失敗
        </Badge>
      );
    default:
      return (
        <Badge variant="outline">
          <AlertCircle className="mr-1 h-3 w-3" />
          {status}
        </Badge>
      );
  }
}
